/** @jsx jsx */
import React, { useState } from 'react';
import { cssMap, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import EditFilledIcon from '@atlaskit/icon/core/migration/edit--edit-filled';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { EngagementSpotlight } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/engagement-spotlight/main.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { PROJECT_PAGES_SPOTLIGHT_KEY } from './constants.tsx';
import { EndUserActivateNudgeProvider } from './end-user-activate-nudge-provider/index.tsx';
import { messages } from './messages.tsx';
import type { PagesMenuItemProps } from './types.tsx';

const styles = cssMap({
	editItemIcon: {
		height: '44px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-edit-icon]': {
			display: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover [data-item-elem-after]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			'& > :not([data-edit-icon])': {
				display: 'none',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& [data-edit-icon]': {
				display: 'flex',
			},
		},
	},
	hoverIconAfter: {
		height: '44px',
		// Display button when the menu item is focused
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-elem-after]': {
			opacity: 0,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover [data-item-elem-after], &:focus-within [data-item-elem-after]': {
			opacity: 1,
		},
	},
	lozenge: {
		paddingLeft: token('space.100'),
	},
});

const EditIcon = ({ onClick }: { onClick: PagesMenuItemProps['onEditClick'] }) => {
	const { formatMessage } = useIntl();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleClick = (ev: any) => {
		ev.preventDefault();
		if (onClick) {
			onClick(ev);
		}
	};
	// this function handle keydown event when user navigate to the edit button and press enter to open the dialog.
	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		if (event.key === 'Enter') {
			handleClick(event);
		}
	};

	const buttonProps: React.ComponentProps<typeof Button> = {
		appearance: 'subtle-link',
		spacing: 'none',
		onClick: handleClick,
		onKeyDown: handleKeyDown,
	};
	return (
		<Button
			data-edit-icon
			{...buttonProps}
			aria-describedby="navigation-apps-sidebar-common.ui.shortcuts.business-pages-link"
		>
			<EditFilledIcon
				color={token('color.icon')}
				LEGACY_size="medium"
				spacing="spacious"
				label={formatMessage(messages.editProjectPages)}
			/>
		</Button>
	);
};

const WrappedPagesMenuItem = ({
	onEditClick,
	iconAfter,
	pagesMenuItemTitle,
	// Extracting here so that it does not end up in the "rest" which would eventually get passed to a normal DOM element and cause React to be unhappy
	projectId,
	...rest
}: PagesMenuItemProps) => {
	const { formatMessage } = useIntl();
	const extraProps =
		!iconAfter && onEditClick ? { iconAfter: <EditIcon onClick={onEditClick} /> } : { iconAfter };
	return (
		<>
			<SpotlightTarget name={PROJECT_PAGES_SPOTLIGHT_KEY}>
				<MenuItem {...rest} {...extraProps} css={styles.hoverIconAfter}>
					<Text id="shortcuts.business-pages-spotlight-text">
						{formatMessage(pagesMenuItemTitle || messages.projectPagesItem)}
					</Text>
				</MenuItem>
			</SpotlightTarget>
			<EngagementSpotlight engagementId={PROJECT_PAGES_SPOTLIGHT_KEY} />
		</>
	);
};

const WrappedPagesMenuItemWithNudge = (props: PagesMenuItemProps) => {
	// for a11y purposes, we drill the focussed state down so the nudge can be shown via keyboard controls
	const [isFocussed, setIsFocussed] = useState<boolean>(false);
	return (
		<ErrorBoundary
			id="pages-menu-item-end-user-activate-nudge"
			packageName="jira-navigation-apps-sidebar-common"
			render={() => <WrappedPagesMenuItem {...props} />}
		>
			<EndUserActivateNudgeProvider isFocussed={isFocussed}>
				<WrappedPagesMenuItem
					{...props}
					onFocus={(e) => {
						setIsFocussed(true);
						props.onFocus?.(e);
					}}
					onBlur={(e) => {
						setIsFocussed(false);
						props.onBlur?.(e);
					}}
				/>
			</EndUserActivateNudgeProvider>
		</ErrorBoundary>
	);
};

export const PagesMenuItem = (props: PagesMenuItemProps) => {
	const [{ route }] = useRouter();

	// don't show the nudge if the user is already on project-pages
	return route.name !== 'project-pages' ? (
		<WrappedPagesMenuItemWithNudge {...props} />
	) : (
		<WrappedPagesMenuItem {...props} />
	);
};
